import { Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, forwardRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import dxDataGrid from 'devextreme/ui/data_grid';
import { IDataSource, IGridColumnConfig, ILoanAmountGridConfig } from '../../../../framework/models/form-template';
import { DynamicFormDataSourceService } from '../../../../framework/services/dynamic-form-data-source.service';
import { UtilitiesService } from '../../framework/services/utilities.service';
import { FeatureFlagsService } from '../../../services/feature-flags.service';

@Component({
  standalone: false,
  selector: 'app-loan-amount-grid',
  templateUrl: './loan-amount-grid.component.html',
  styleUrl: './loan-amount-grid.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LoanAmountGridComponent),
      multi: true
    }
  ]
})
export class LoanAmountGridComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() config: ILoanAmountGridConfig; // From FormTemplate.Metadata
  /**
   * Data source definitions from the dynamic form config.
   * The component will resolve each unique lookupSource key used in
   * config.gridColumns automatically via DynamicFormDataSourceService.
   */
  @Input() dataSources: IDataSource[] = [];
  /**
   * Live form data passed down from the parent so that REST data sources
   * that declare `dynamicParams` can include the current field values
   * as query parameters (e.g. filtering loans by dealNumber).
   */
  @Input() formContext: Record<string, any> = {};
  @Input() refreshToken: number = 0;
  /**
   * @deprecated Pass dataSources instead. Kept for backwards compatibility
   * with static forms that still inject loan data directly.
   */
  @Input() loanDataSource: any[];
  @Input() showInactiveLoans: boolean = false;

  /** Emits the updated value array so parent templates can use [(value)] binding */
  @Output() valueChange = new EventEmitter<any[]>();

  isGridInEditMode: boolean = false;

  /** Cached column definitions – rebuilt whenever config or resolved data changes */
  columns: any[] = [];

  /** Resolved data keyed by IDataSource.sourceKey */
  private resolvedData = new Map<string, any[]>();
  private resolveGeneration = 0;

  private gridInstance: dxDataGrid;
  private _value: any[] = [];
  private onChange: (value: any) => void;
  private onTouched: () => void;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly dataSourceService: DynamicFormDataSourceService,
    private readonly featureFlagsService: FeatureFlagsService
  ) { }

  get value(): any[] {
    return this._value ?? [];
  }

  get hasAvailableItems(): boolean {
    const lookupSources = (this.config?.gridColumns ?? [])
      .filter((c: IGridColumnConfig) => c.lookupSource)
      .map((c: IGridColumnConfig) => c.lookupSource);
    if (!lookupSources.length) return true;
    return lookupSources.some((src: string) => this.getFilteredSourceData(src).length > 0);
  }

  get requiresMandateSelection(): boolean {
    return this.config?.allowMandateSelection === true;
  }

  get hasSelectedMandate(): boolean {
    if (!this.requiresMandateSelection) {
      return true;
    }

    const mandate = this.formContext?.['mandate'];
    return typeof mandate === 'string' ? mandate.trim().length > 0 : !!mandate;
  }

  get canEditGrid(): boolean {
    return this.hasSelectedMandate;
  }

  get maxRows(): number | null {
    return this.config?.validationRules?.maxRows ?? null;
  }

  get canAddRows(): boolean {
    if (!this.canEditGrid) {
      return false;
    }

    if (this.maxRows == null) {
      return true;
    }

    return (this.value?.length ?? 0) < this.maxRows;
  }

  @Input()
  set value(val: any[]) {
    this._value = val ?? [];
  }

  // ControlValueAccessor implementation
  writeValue(value: any[]): void {
    this._value = value || [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  ngOnInit(): void {
    this.columns = this.buildColumns();
    this.resolveDataSources();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-resolve whenever data source definitions or column config changes
    if (changes['dataSources'] || changes['config']) {
      this.columns = this.buildColumns();
      this.resolveDataSources();
    }

    if (changes['loanDataSource'] && !changes['loanDataSource'].firstChange) {
      this.columns = this.buildColumns();
      this.refreshGridAvailability();
    }

    // Force a fresh resolve when parent indicates context-changing input
    // such as mandate selection has changed.
    if (changes['refreshToken'] && !changes['refreshToken'].firstChange) {
      this.resolveDataSources();
    }

    // Refresh the grid's lookup when showInactiveLoans toggles
    if (changes['showInactiveLoans'] && !changes['showInactiveLoans'].firstChange && this.gridInstance) {
      this.refreshGridAvailability();
    }
  }

  /**
   * Resolves each unique lookupSource key referenced in config.gridColumns.
   * Results are stored in resolvedData and the grid is refreshed once ready.
   */
  private resolveDataSources(): void {
    if (!this.dataSources?.length) return;

    // Don't fire any data source calls until config has arrived — without it
    // we can't determine whether mandate selection is required.
    if (!this.config) return;

    // Don't fire any data source calls until the user has selected a mandate —
    // the grid is non-editable anyway, and a mandateless call would fall
    // through to a rejected backend request.
    if (this.requiresMandateSelection && !this.hasSelectedMandate) return;

    const generation = ++this.resolveGeneration;
    this.resolvedData.clear();
    this.columns = this.buildColumns();
    if (this.gridInstance && !this.isGridInEditMode) {
      this.refreshGridAvailability();
    }

    // Resolve every active data source in the list.
    // We no longer gate on config.gridColumns being available so the HTTP call
    // fires as soon as dataSources arrives, regardless of config timing.
    this.dataSources
      .filter(ds => ds.isActive)
      .forEach(ds => {
        this.dataSourceService.resolve(ds, this.formContext).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: data => {
            if (generation !== this.resolveGeneration) {
              return;
            }
            this.resolvedData.set(ds.sourceKey, data);
            // Pre-populated rows (e.g. editing an existing task) never go through
            // setCellValue, so backfill their display text once the lookup resolves.
            this.backfillLoanNumberDisplay(ds.sourceKey, data);
            // Rebuild columns so lookup closures capture the freshly resolved data.
            // Don't refresh the grid while the user is mid-add-row – that would
            // cancel the in-progress edit.
            this.columns = this.buildColumns();
            if (this.gridInstance && !this.isGridInEditMode) {
              this.refreshGridAvailability();
            }
          },
          error: err => {
            if (generation !== this.resolveGeneration) {
              return;
            }
            console.error(`[LoanAmountGridComponent] Failed to resolve "${ds.sourceKey}":`, err);
          }
        });
      });
  }

  onGridInitialized(e: any): void {
    this.gridInstance = e.component;
    this.refreshGridAvailability();
  }

  /**
   * Stamps `_loanNumberDisplay` (the resolved "Loan (Deal)" column text) onto any
   * row whose loanNumber lookup resolves against the given source but hasn't been
   * stamped yet — covers rows hydrated from stored data (edit mode) rather than
   * picked interactively via setCellValue.
   */
  private backfillLoanNumberDisplay(sourceKey: string, data: any[]): void {
    const col = this.config?.gridColumns?.find(
      (c: IGridColumnConfig) => c.type === 'lookup' && c.field === 'loanNumber' && c.lookupSource === sourceKey
    );
    if (!col || !this._value?.length) return;

    const ds = this.dataSources?.find(d => d.sourceKey === sourceKey);
    const valueExpr = ds?.configuration?.valueExpr ?? (col as any).valueExpr ?? 'name';
    const displayFn = this.buildLookupDisplayFn(col);

    this._value.forEach((row: any) => {
      if (row['_loanNumberDisplay'] || !row[col.field]) return;
      const match = data.find((d: any) => d[valueExpr] === row[col.field]);
      if (match) {
        row['_loanNumberDisplay'] = displayFn(match);
      }
    });
  }

  /** Builds the same lookup -> display-text function used by the grid's cellTemplate/editorOptions. */
  private buildLookupDisplayFn(col: IGridColumnConfig): (item: any) => string {
    const ds = this.dataSources?.find(d => d.sourceKey === col.lookupSource);
    const displayExprTemplate = ds?.configuration?.displayExprTemplate ?? (col as any).displayExprTemplate;
    const displayExprField = ds?.configuration?.displayExpr ?? (col as any).displayExpr ?? 'name';
    const isDealLookup = col.lookupSource === 'deals' || col.field === 'dealNumber' || col.field === 'dealName';

    return (item: any) => {
      if (!item) return '';
      if (displayExprTemplate) {
        return this.compileDisplayExpr(displayExprTemplate)(item);
      }
      if (isDealLookup) {
        return UtilitiesService.formatDealDisplay(item);
      }
      return item?.[displayExprField] ?? '';
    };
  }

  private refreshGridAvailability(): void {
    if (!this.gridInstance || this.isGridInEditMode) {
      return;
    }

    this.gridInstance.option('editing.allowAdding', this.canAddRows && this.hasAvailableItems);
    this.gridInstance.refresh();
  }

  onEditingStart(): void {
    this.isGridInEditMode = true;
  }

  onInitNewRow(): void {
    this.isGridInEditMode = true;
  }

  onEditCanceled(): void {
    this.isGridInEditMode = false;
  }

  onSaved(): void {
    this.isGridInEditMode = false;
  }

  onRowInserted(e: any): void {
    if (!e.data.key) {
      e.data.key = this.generateUniqueId();
    }
    this.expandMultiBorrowerSelection(e);
    // Emit the same array reference that DevExtreme is managing internally.
    // Creating a spread copy would swap out the dataSource reference mid-operation
    // and force the grid to reinitialise, cancelling the current add.
    if (this.onChange) this.onChange(this._value);
    this.valueChange.emit(this._value);
    if (this.onTouched) this.onTouched();
  }

  onRowUpdated(e?: any): void {
    this.expandMultiBorrowerSelection(e);
    if (this.onChange) this.onChange(this._value);
    this.valueChange.emit(this._value);
    if (this.onTouched) this.onTouched();
  }

  /**
   * Fans a multi-borrower checklist selection out into one row per borrower:
   * the first selection stays on the saved row, the rest are cloned onto new
   * rows (capped at maxRows) so `_value` keeps one row per loan+borrower pair.
   */
  private expandMultiBorrowerSelection(e: any): void {
    if (!e?.data || !Array.isArray(e.data.borrowerName)) return;

    const selected: any[] = e.data.borrowerName;
    e.data.borrowerName = selected[0] ?? null;

    const extra = selected.slice(1);
    if (!extra.length) return;

    const capacity = this.maxRows != null ? this.maxRows - this._value.length : extra.length;
    const toAdd = Math.max(0, Math.min(extra.length, capacity));
    for (let i = 0; i < toAdd; i++) {
      this._value.push({ ...e.data, key: this.generateUniqueId(), borrowerName: extra[i] });
    }

    if (this.gridInstance) {
      this.gridInstance.refresh();
    }
  }

  onRowRemoved(e?: any): void {
    if (this.onChange) this.onChange(this._value);
    this.valueChange.emit(this._value);
    if (this.onTouched) this.onTouched();
  }

  onShowInactiveChanged(e: any): void {
    this.showInactiveLoans = e.value;
    if (this.gridInstance) {
      this.gridInstance.refresh();
    }
  }

  onToolbarPreparing(e: any): void {
    const title = this.config?.fieldLabel?.trim() || 'Loans';
    const toolbarItems = e.toolbarOptions?.items ?? [];

    if (toolbarItems.some((item: any) => item?.cssClass === 'loan-grid-toolbar-title-item')) {
      return;
    }

    toolbarItems.unshift({
      location: 'before',
      cssClass: 'loan-grid-toolbar-title-item',
      template: () => {
        const titleElement = document.createElement('div');
        titleElement.className = 'loan-grid-toolbar-title';
        titleElement.textContent = title;
        return titleElement;
      }
    });

    e.toolbarOptions.items = toolbarItems;
  }

  private buildColumns(): any[] {
    if (!this.config?.gridColumns) return [];

    return this.config.gridColumns.map((col: any) => {
      // validationRules are declared per-column in the payload, e.g.
      // { "type": "required", "message": "Amount is required" }
      const validationRules = col.validationRules ?? [];
      // caption is optional in the payload; falls back to a humanised field name
      // e.g. "loanNumber" → "Loan Number",  "amount" → "Amount"
      const caption = col.caption || this.humanizeFieldName(col.field);

      if (col.type === 'lookup') {
        const ds = this.dataSources?.find(d => d.sourceKey === col.lookupSource);
        const valueExpr = ds?.configuration?.valueExpr ?? col.valueExpr ?? 'name';

        // Build a reusable display function once so both templates share it
        const displayFn = this.buildLookupDisplayFn(col);

        // setCellValue: when a user picks a lookup value, copy the configured
        // fields from the picked source item onto the row. Used by the
        // Amendment of Security form to auto-populate `borrowerName` from the
        // selected loan.
        const setCellValueFields = (col as IGridColumnConfig).setCellValueFields;
        // The "Loan (Deal)" column (field: loanNumber) always stamps its own
        // resolved display text onto the row, independent of setCellValueFields,
        // so callers can read the exact grid text (e.g. for FullReferenceName).
        const isPrimaryLoanLookup = col.field === 'loanNumber';
        const setCellValue = (setCellValueFields?.length || isPrimaryLoanLookup)
          ? (newData: any, value: any, currentRowData: any) => {
              newData[col.field] = value;
              const sourceData = this.resolvedData.get(col.lookupSource)
                ?? this.getLegacyLoanDataSourceFallback();
              const picked = sourceData.find((d: any) => d[valueExpr] === value);

              if (isPrimaryLoanLookup) {
                newData['_loanNumberDisplay'] = picked ? displayFn(picked) : null;
              }

              if (!setCellValueFields?.length) {
                return;
              }

              if (picked) {
                // When the picked loanRef has multiple borrower/property variants in
                // the source, leave the field blank so the user must choose from the
                // per-row dropdown (see onEditorPreparing/preparePropertyEditor).
                const borrowerVariants = this.getBorrowerVariantsForLoan(col.lookupSource, value);
                const propertyVariants = this.getPropertyVariantsForLoan(col.lookupSource, value);
                for (const map of setCellValueFields) {
                  if (map.to === 'borrowerName' && borrowerVariants.length > 1) {
                    newData[map.to] = null;
                  } else if (map.to === 'bookName' && propertyVariants.length > 1) {
                    newData[map.to] = null;
                  } else {
                    newData[map.to] = picked[map.from] ?? null;
                  }
                }
              } else {
                for (const map of setCellValueFields) {
                  newData[map.to] = null;
                }
              }
            }
          : undefined;

        return {
          dataField: col.field,
          caption,
          validationRules,
          allowEditing: col.allowEditing !== false,
          // cellTemplate: renders the selected value inside the grid row.
          // Inactive items get red text.
          cellTemplate: (container: any, options: any) => {
            const sourceData = this.resolvedData.get(col.lookupSource)
              ?? this.getLegacyLoanDataSourceFallback();
            const item = sourceData.find((d: any) => d[valueExpr] === options.value);
            const text = item ? displayFn(item) : (options.value ?? '');
            const color = this.isItemInactive(item) ? 'var(--color-inactive, #d32f2f)' : 'inherit';
            const span = document.createElement('span');
            span.style.color = color;
            span.textContent = text;
            container.appendChild(span);
          },
          lookup: {
            dataSource: () => this.getFilteredSourceData(col.lookupSource),
            valueExpr,
            displayExpr: displayFn
          },
          ...(setCellValue ? { setCellValue } : {}),
          ...(col.width ? { width: col.width } : {}),
          // editorOptions.itemTemplate: renders each row inside the dropdown list.
          // Inactive items get red text here too.
          editorOptions: {
            itemTemplate: (itemData: any, _index: number, itemElement: any) => {
              const text = displayFn(itemData);
              const color = this.isItemInactive(itemData) ? 'var(--color-inactive, #d32f2f)' : 'inherit';
              const span = document.createElement('span');
              span.style.color = color;
              span.textContent = text;
              itemElement.appendChild(span);
            }
          }
        };
      }

      if (col.type === 'number' && col.format === 'currency') {
        const currencyFormat = { type: 'currency', currency: this.featureFlagsService.getDefaultCurrency(), precision: 2 };
        return {
          dataField: col.field,
          caption,
          dataType: 'number',
          validationRules,
          allowEditing: col.allowEditing !== false,
          // format: applied to the read-only cell display
          format: currencyFormat,
          // editorOptions: applied to the dxNumberBox shown while editing.
          // Without this the editor shows a plain unformatted number.
          editorOptions: {
            format: currencyFormat
          }
        };
      }

      // Force editability for any column targeted by a setCellValueFields
      // mapping (e.g. borrowerName). The cell stays read-only via
      // onEditorPreparing when only one borrower variant exists for the
      // selected loan; the editor is swapped to a dxSelectBox when there are
      // multiple variants.
      const isSetCellValueTarget = this.config.gridColumns.some((c: any) =>
        (c as IGridColumnConfig).setCellValueFields?.some(m => m.to === col.field)
      );
      const allowEditing = isSetCellValueTarget ? true : (col.allowEditing !== false);

      // bookName (captioned "Property" on some templates) can hold a multi-select
      // array of property names; render one per line for the read-only grid cell.
      const cellTemplate = col.field === 'bookName'
        ? (container: any, options: any) => {
            const values = Array.isArray(options.value) ? options.value : (options.value != null ? [options.value] : []);
            values.forEach((value: string) => {
              const line = document.createElement('div');
              line.textContent = value;
              container.appendChild(line);
            });
          }
        : undefined;

      return {
        dataField: col.field,
        caption,
        dataType: col.type,
        validationRules,
        allowEditing,
        ...(cellTemplate ? { cellTemplate } : {})
      };
    });
  }

  /** Stamps data-testid on header and data cells for targeted test selection. */
  onCellPrepared(e: any): void {
    if (!e.column?.dataField) return;
    if (e.rowType === 'header') {
      e.cellElement.setAttribute('data-testid', `loan-amount-grid-header-${e.column.dataField}`);
    } else if (e.rowType === 'data') {
      e.cellElement.setAttribute('data-testid', `loan-amount-grid-cell-${e.column.dataField}-${e.rowIndex}`);
    }
  }

  /** Stamps data-testid on data rows and the add/insert row for targeted test selection. */
  onRowPrepared(e: any): void {
    if (e.rowType === 'data') {
      e.rowElement.setAttribute('data-testid', `loan-amount-grid-row-${e.rowIndex}`);
    } else if (e.rowType === 'insert') {
      e.rowElement.setAttribute('data-testid', 'loan-amount-grid-row-new');
    }
  }

  /**
   * Returns filtered data for a given sourceKey.
   *
   * Resolution priority:
   *  1. Data resolved from dataSources (DynamicFormDataSourceService)
   *  2. Fallback to legacy loanDataSource @Input (static forms) — never used for
   *     mandate-gated columns, since that data isn't scoped to any mandate.
   */
  private getLegacyLoanDataSourceFallback(): any[] {
    return this.requiresMandateSelection ? [] : this.loanDataSource ?? [];
  }

  private getFilteredSourceData(sourceKey?: string): any[] {
    const raw = (sourceKey && this.resolvedData.get(sourceKey)) ?? this.getLegacyLoanDataSourceFallback();

    // Exclude already-selected rows from the lookup dropdown
    const lookupColumn = this.config?.gridColumns?.find((c: IGridColumnConfig) => c.lookupSource === sourceKey);
    const lookupField = lookupColumn?.field;
    const ds = this.dataSources?.find((d: any) => d.sourceKey === sourceKey);
    const lookupValueExpr =
      ds?.configuration?.valueExpr ??
      (lookupColumn as any)?.lookupValueExpr ??
      (lookupColumn as any)?.lookup?.valueExpr ??
      'name';

    // Variant-aware exclusion: a loanRef is only removed from the dropdown
    // when every borrower variant for it has already been used on the grid.
    // Loans with a single borrower behave exactly as before.
    const allLoanRefs = lookupField
      ? Array.from(new Set((this._value || []).map((row: any) => row[lookupField]).filter(Boolean)))
      : [];

    const exhaustedLoanRefs = allLoanRefs.filter((loanVal: any) => {
      const variants = this.getBorrowerVariantsForLoan(sourceKey, loanVal);
      if (variants.length <= 1) return true;
      const usedBorrowers = (this._value || [])
        .filter((r: any) => r[lookupField as string] === loanVal && r.borrowerName)
        .map((r: any) => r.borrowerName);
      return new Set(usedBorrowers).size >= variants.length;
    });

    let filtered = exhaustedLoanRefs.length
      ? raw.filter((item: any) => !exhaustedLoanRefs.includes(item?.[lookupValueExpr]))
      : raw;

    // Dedupe by lookup value so a loanRef with multiple borrower rows in the
    // source data appears only once in the dropdown.
    if (lookupValueExpr) {
      const seen = new Set<any>();
      filtered = filtered.filter((item: any) => {
        const k = item?.[lookupValueExpr];
        if (k === undefined || k === null) return true;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    }

    const filterByField = lookupColumn?.filterByRowField;
    if (filterByField && (this._value || []).length > 0) {
      const existingFieldValues = (this._value || [])
        .map((row: any) => row[filterByField])
        .filter(Boolean);
      if (existingFieldValues.length > 0) {
        filtered = filtered.filter((item: any) => existingFieldValues.includes(item?.[filterByField]));
      }
    }

    if (this.showInactiveLoans) return filtered;

    return filtered.filter((item: any) => !this.isItemInactive(item));
  }

  private generateUniqueId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Returns the distinct borrower-name variants found in the resolved source
   * data for a given lookup value (loanRef). Used to detect duplicate loanRefs
   * with multiple borrowers, which triggers the borrower dropdown UX.
   *
   * The borrower target field is inferred from the loan column's
   * setCellValueFields mapping (`to: 'borrowerName'`).
   */
  private getBorrowerVariantsForLoan(sourceKey: string | undefined, loanValue: any): any[] {
    if (!sourceKey || loanValue === undefined || loanValue === null) return [];
    const col = this.config?.gridColumns?.find(
      (c: IGridColumnConfig) => c.lookupSource === sourceKey
    );
    const borrowerField = col?.setCellValueFields?.find(m => m.to === 'borrowerName')?.from;
    if (!col || !borrowerField) return [];
    const ds = this.dataSources?.find(d => d.sourceKey === sourceKey);
    const valueExpr = ds?.configuration?.valueExpr ?? col.valueExpr ?? 'name';
    const raw = this.resolvedData.get(sourceKey) ?? this.getLegacyLoanDataSourceFallback();
    const variants = raw
      .filter((d: any) => d[valueExpr] === loanValue)
      .map((d: any) => d[borrowerField])
      .filter((b: any) => b !== undefined && b !== null && b !== '');
    return Array.from(new Set(variants));
  }

  /**
   * Returns the property names available for a given lookup value (loanRef),
   * read from the resolved source row's `propertyNames` array. Falls back to
   * wrapping the row's scalar `bookName` in a single-element array for data
   * sources that don't expose `propertyNames` (e.g. non-EU mandates).
   */
  private getPropertyVariantsForLoan(sourceKey: string | undefined, loanValue: any): string[] {
    if (!sourceKey || loanValue === undefined || loanValue === null) return [];
    const ds = this.dataSources?.find(d => d.sourceKey === sourceKey);
    const col = this.config?.gridColumns?.find((c: IGridColumnConfig) => c.lookupSource === sourceKey);
    const valueExpr = ds?.configuration?.valueExpr ?? col?.valueExpr ?? 'name';
    const raw = this.resolvedData.get(sourceKey) ?? this.getLegacyLoanDataSourceFallback();
    const row = raw.find((d: any) => d[valueExpr] === loanValue);
    if (!row) return [];
    if (Array.isArray(row.propertyNames) && row.propertyNames.length) {
      return Array.from(new Set(row.propertyNames.filter((p: any) => p !== undefined && p !== null && p !== '')));
    }
    return row.bookName ? [row.bookName] : [];
  }

  /**
   * Editor swap: when the selected loanRef has multiple borrower variants in
   * the source data, replace the borrowerName editor with a dxTagBox checklist
   * (checkboxes + confirm button) of the available borrowers, excluding those
   * already chosen on other rows for the same loanRef, so the user can select
   * several at once. When only one variant exists, the cell stays read-only to
   * preserve the auto-populate UX.
   */
  onEditorPreparing(e: any): void {
    if (!this.canEditGrid) {
      e.editorOptions = { ...(e.editorOptions ?? {}), readOnly: true, disabled: true };
      return;
    }

    if (e.parentType !== 'dataRow') return;

    if (e.dataField === 'bookName') {
      this.preparePropertyEditor(e);
      return;
    }

    if (e.dataField !== 'borrowerName') return;

    const loanCol = this.config?.gridColumns?.find((c: IGridColumnConfig) =>
      c.type === 'lookup' && c.setCellValueFields?.some(m => m.to === 'borrowerName')
    );
    if (!loanCol) return;

    const rowLoan = e.row?.data?.[loanCol.field];
    const variants = rowLoan
      ? this.getBorrowerVariantsForLoan(loanCol.lookupSource, rowLoan)
      : [];

    // No borrower found at all: leave the default editor so the user can type one in.
    if (!variants.length) return;

    if (variants.length === 1) {
      e.editorOptions = { ...(e.editorOptions ?? {}), readOnly: true };
      return;
    }

    // Exclude borrowers already chosen on OTHER rows for the same loanRef
    const usedByOthers = (this._value || [])
      .filter((r: any, idx: number) =>
        idx !== e.row.rowIndex &&
        r[loanCol.field] === rowLoan &&
        r.borrowerName)
      .map((r: any) => r.borrowerName);

    const available = variants.filter(b => !usedByOthers.includes(b));
    const previousOnValueChanged = e.editorOptions?.onValueChanged;

    // 'auto' does not reliably measure dx-list-item text width in a grid-hosted
    // popup, so size the popup from the longest borrower name instead.
    const longestLabelLength = Math.max(...available.map(v => String(v).length));
    const popupWidth = Math.min(600, Math.max(300, longestLabelLength * 8 + 80));

    e.editorName = 'dxTagBox';
    e.editorOptions = {
      ...(e.editorOptions ?? {}),
      // Without an explicit width the tag box collapses to its placeholder's
      // content size (a tiny box) instead of filling the grid cell like a
      // normal dropdown editor.
      width: '100%',
      // Popup width is independent of the field width; without this it
      // inherits the narrow grid-cell width and truncates borrower names.
      dropDownOptions: { width: popupWidth },
      dataSource: available,
      value: e.value ? [e.value] : [],
      searchEnabled: false,
      showSelectionControls: true,
      showDropDownButton: true,
      applyValueMode: 'useButtons',
      showClearButton: true,
      onValueChanged: (args: any) => {
        e.setValue(args.value);
        if (previousOnValueChanged) previousOnValueChanged(args);
      }
    };
  }

  /**
   * Editor swap: replaces the Property (bookName) editor with a dxTagBox
   * multi-select populated from the selected loan's property names, but only
   * when more than one property exists; a single property stays read-only
   * (auto-populated) and no values leaves the default free-text editor, the
   * same 0/1/many contract as the borrowerName editor above. The full
   * selected array is stored on the single row (no row fan-out).
   */
  private preparePropertyEditor(e: any): void {
    const loanCol = this.config?.gridColumns?.find((c: IGridColumnConfig) =>
      c.type === 'lookup' && c.setCellValueFields?.some(m => m.to === 'bookName')
    );
    if (!loanCol) return;

    const rowLoan = e.row?.data?.[loanCol.field];
    const variants = rowLoan
      ? this.getPropertyVariantsForLoan(loanCol.lookupSource, rowLoan)
      : [];

    if (!variants.length) return;

    if (variants.length === 1) {
      e.editorOptions = { ...(e.editorOptions ?? {}), readOnly: true };
      return;
    }

    const previousOnValueChanged = e.editorOptions?.onValueChanged;

    // 'auto' does not reliably measure dx-list-item text width in a grid-hosted
    // popup, so size the popup from the longest property name instead.
    const longestLabelLength = Math.max(...variants.map(v => String(v).length));
    const popupWidth = Math.min(600, Math.max(300, longestLabelLength * 8 + 80));

    e.editorName = 'dxTagBox';
    e.editorOptions = {
      ...(e.editorOptions ?? {}),
      // Without an explicit width the tag box collapses to its placeholder's
      // content size (a tiny box) instead of filling the grid cell like a
      // normal dropdown editor.
      width: '100%',
      // Popup width is independent of the field width; without this it
      // inherits the narrow grid-cell width and truncates property names.
      dropDownOptions: { width: popupWidth },
      dataSource: variants,
      value: Array.isArray(e.value) ? e.value : (e.value ? [e.value] : []),
      searchEnabled: false,
      showSelectionControls: true,
      showDropDownButton: true,
      applyValueMode: 'useButtons',
      showClearButton: true,
      onValueChanged: (args: any) => {
        e.setValue(args.value);
        if (previousOnValueChanged) previousOnValueChanged(args);
      }
    };
  }

  /**
   * Returns true when an item from the data source should be considered
   * inactive. Handles both API conventions:
   *  - isActive: boolean  (true = active)
   *  - status: string     ("Inactive" = inactive)
   */
  private isItemInactive(item: any): boolean {
    if (!item) return false;
    if ('isActive' in item) return item.isActive === false;
    if ('status' in item)   return item.status?.toLowerCase() === 'inactive';
    return false;
  }

  /**
   * Converts a camelCase or PascalCase field name to Title Case words.
   * Used as the default column caption when none is supplied in the payload.
   *
   * Examples:
   *   "loanNumber"  → "Loan Number"
   *   "amount"      → "Amount"
   *   "effectiveDate" → "Effective Date"
   */
  private humanizeFieldName(field: string): string {
    if (!field) return '';
    // Insert a space before each uppercase letter sequence, then title-case
    return field
      .replace(/([A-Z]+)/g, ' $1')
      .replace(/^./, s => s.toUpperCase())
      .trim();
  }

  /**
   * Compiles a displayExprTemplate string into a DevExtreme-compatible
   * displayExpr function.
   *
   * Template syntax: use {fieldName} placeholders, e.g.
   *   "{name} ({status})"  →  item => `${item.name} (${item.status})`
   *
   * This lets displayExpr be stored as plain JSON in the DB config instead
   * of requiring an inline arrow function in component code.
   */
  private compileDisplayExpr(template: string): (item: any) => string {
    return (item: any): string => {
      if (!item) return '';
      return template.replace(/\{(\w+)\}/g, (_, field) => item[field] ?? '');
    };
  }
}